import { Controller, FieldError, useFormContext, useWatch } from "react-hook-form";
import { FormHelperText, InputAdornment, SxProps, TextField } from "@mui/material";
import { useMemo, useState } from "react";
import { FnComponents, I18n } from "utilities/utilities";
import { I18NextNs, RegularExpression, TypeComponent } from "enum/enum";
import { CommaSeparateNumber, isNumber } from "components/Input/InputNumber";

const NumberInput = (props: IInputNumberCustom) => {
  const {
    disabled = false,
    defaultValue = null,
    id,
    IsAllowScientific = false,
    SciMessage = null,
    IsShowMessageError = true,
    placeholder,
    textAlign = "right",
    variant = "outlined",
    label = "",
    maxLength,
    max = null,
    maxDigits = 5,
    min = null,
    name,
    required,
    size = "small",
    IsLeadingZero = false,
    IsComma = true,
    validateMessage = null,
    IsShowTextCont = true,
    IsShrink,
  } = props;
  const { control, register, getValues } = useFormContext();

  const rules = useMemo(() => {
    return {
      required: {
        value: required,
        message: `${I18n.SetText("required", I18NextNs.validation)} ${label}`,
      },
      pattern: {
        value: IsAllowScientific ? RegularExpression.NumberScientific : RegularExpression.Number,
        message: `${I18n.SetText("patternNumber", I18NextNs.validation)}`,
      },
      validate: (value) => {
        if (value) {
          let sValue = value;
          let sVal = (sValue + "").replaceAll(/,/g, "");
          sVal = sVal.replaceAll(",", "");
          if (isNumber(sVal)) {
            let nVal = +sVal;
            if (min) {
              return (
                nVal >= min
              );
            }

            if (max) {
              return (
                nVal <= max
              );
            }
          }
        }
      },
    };
  }, [required, label, IsAllowScientific, min, max]);

  const handleOnChange = (e, onChangeController) => {
    let sValue = e.target.value;
    let sVal = (sValue + "").replaceAll(/,/g, "");
    sVal = sVal.replaceAll(",", "");
    e.target.value = sVal;
    onChangeController(e);
  };

  const handleOnBlur = (e) => {

    if (props.onBlur) {
      props.onBlur(e.target.value, e);
    }
  };

  const genMessage = (min, max, validateMessage) => {
    let sMessage = "";
    if (min && max) {
      sMessage = validateMessage !== null ? `${validateMessage}` : `${I18n.SetText("inputMinMax", I18NextNs.validation)} ${min} ${I18n.SetText("to")} ${max} `;
    }
    else if (min && !max) {
      sMessage = validateMessage !== null ? `${validateMessage}` : `${I18n.SetText("inputMin", I18NextNs.validation)} ${min}`;

    }
    else if (!min && max) {
      sMessage = validateMessage !== null ? `${validateMessage}` : `${I18n.SetText("inputMax", I18NextNs.validation)} ${max} `;
    }
    return sMessage;
  }

  const getMessageValidate = (error: FieldError) => {
    let sMessage = "";
    if (IsShowMessageError) {
      if (error) {
        switch (error.type) {
          case "required":
            sMessage = `${I18n.SetText("required", I18NextNs.validation)} ${label}`;
            break;
          case "pattern":
            sMessage = `${I18n.SetText("patternNumber", I18NextNs.validation)}`;
            if (IsAllowScientific) {
              sMessage = SciMessage !== null ? `${SciMessage}` : `${I18n.SetText("patternScientific", I18NextNs.validation)}`;
            }
            break;
          case "validate":
            sMessage = genMessage(min, max, validateMessage);
            break;
        }
      }
    }
    return sMessage;
  };

  const FieldName = useWatch({
    control,
    name: name,
  });
  const [focus, setFocus] = useState(false);
  const sCounter = `"${getValues(name) ? getValues(name).length : 0}/${props.maxLength}"`;
  const maxLengthText: SxProps = {
      "::after": {
          content: IsShowTextCont && props.maxLength
              ? sCounter : '""',
          position: "absolute",
          bottom: "5px",
          right: "10px",
          color: "#989898",
          fontSize: "10px",
      },
  };

  return (
    <Controller
      key={FnComponents.GetKey(TypeComponent.inputNumber, name)}
      name={name}
      control={control}
      rules={rules}
      shouldUnregister={true}
      defaultValue={defaultValue}
      render={({
        field: { onChange, onBlur, value, ref },
        fieldState: { invalid, error },
      }) => {
        let IsValid = invalid && !disabled;
        let helperText = getMessageValidate(error);
        let sValue = value ? value + "" : "";

        return (
          <>
            <TextField
              sx={[{ ...maxLengthText }]}
              {...register(name)}
              type={"text"}
              id={FnComponents.GetId(TypeComponent.inputNumber, id, name)}
              inputRef={ref}
              value={CommaSeparateNumber(value, maxDigits, props.ShowDigits, IsLeadingZero, IsComma)}
              error={error?.message != null}
              variant={variant}
              size={size}
              placeholder={placeholder}
              disabled={disabled}
              label={label}
              autoComplete={"off"}
              inputProps={{
                maxLength :maxLength,
                style: {
                  textAlign: textAlign,
                },
              }}
              onChange={(e) => {
                handleOnChange(e, onChange);
                if (props.onChange) {
                  props.onChange(e.target.value, e);
                }
              }}
              InputLabelProps={{
                shrink: IsShrink || sValue.length > 0 || FieldName || focus,
              }}
              onBlur={(e) => {
                if (!IsShrink) setFocus(false);
                handleOnBlur(e);
              }}
              style={{
                width: "100%",
                ...props?.style,
              }}
            />
            {IsShowMessageError && IsValid && (
              <FormHelperText>{helperText}</FormHelperText>
            )}
          </>
        )
      }}
    />
  );
};

export default NumberInput


export interface IInputNumberCustom {
  arrStringAllow?: any;
  defaultValue?: string | number;
  disabled?: boolean;
  endAdornment?: any;
  id?: string;
  IsAllowScientific?: boolean;
  IsCanMinus?: boolean;
  IsLeadingZero?: boolean;
  IsComma?: boolean;
  IsShowMessageError?: boolean;
  IsShrink?: boolean;
  label?: string;
  margin?: any;
  max?: number;
  maxLength?: number;
  maxDigits?: number;
  maxInteger?: number;
  min?: number;
  name: string;
  onBlur?: (value: any, event: any) => void;
  onChange?: (value: any, event: any) => void;
  onFocus?: (event: any) => void;
  onKeyDown?: (event: any) => void;
  onKeyUp?: (event: any) => void;
  placeholder?: string;
  required?: boolean;
  ShowDigits?: number;
  startAdornment?: any;
  size?: "small" | "medium";
  style?: React.CSSProperties;
  suffix?: string;
  textAlign?: "inherit" | "left" | "center" | "right" | "justify";
  variant?: "filled" | "standard" | "outlined" | undefined;
  validateMessage?: string;
  SciMessage?: string;
  IsOverSixMonth?: boolean;
  type?: "NumberScientific" | "Number";
  IsShowTextCont?: boolean;
}
